/**
 * KOAI Dashboard — 사용자 중심 홈
 * 바로 시작 · 이용 현황 · 최근 대화 · 공지 · 사용 가이드
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {
  Announcement as NoticeIcon,
  ArrowForward as ArrowIcon,
  Chat as ChatIcon,
  CloudUpload as UploadIcon,
  Description as DocIcon,
  SaveAlt as ExportIcon,
  Star as UpgradeIcon,
  Person as MyPageIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useAuth } from '@/contexts/AuthContext';
import { noticesAPI, ragAPI, subscriptionAPI } from '../services/api';

function formatRelativeTime(dateString, t) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const diff = Date.now() - date.getTime();
  if (diff < 60000) return t('dashboard.time.justNow');
  if (diff < 3600000) return t('dashboard.time.minutesAgo', { n: Math.floor(diff / 60000) });
  if (diff < 86400000) return t('dashboard.time.hoursAgo', { n: Math.floor(diff / 3600000) });
  return t('dashboard.time.daysAgo', { n: Math.floor(diff / 86400000) });
}

function QuickAction({ icon: Icon, title, description, onClick, color = 'primary.main' }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardActionArea onClick={onClick} sx={{ height: '100%', alignItems: 'stretch' }}>
        <CardContent sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'action.hover',
              color,
              flexShrink: 0,
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const { user, isAdmin, isPaid, plan } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [recentSessions, setRecentSessions] = useState([]);
  const [totalChats, setTotalChats] = useState(0);
  const [notices, setNotices] = useState([]);
  const [chatUsage, setChatUsage] = useState(null);
  const [planName, setPlanName] = useState('');
  const [loadError, setLoadError] = useState('');

  const displayName = user?.name || user?.email?.split('@')[0] || t('dashboard.defaultUser');

  const resolvedPlanName = useMemo(() => {
    if (planName) return planName;
    if (isAdmin) return t('dashboard.plan.admin');
    if (isPaid || plan === 'paid') return t('dashboard.plan.paid');
    return t('dashboard.plan.free');
  }, [planName, isAdmin, isPaid, plan, t]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [sessionsRes, noticesRes, subRes] = await Promise.all([
        ragAPI.getSessions({ limit: 5 }),
        noticesAPI.getAll({ limit: 3, status: 'published' }).catch(() => null),
        subscriptionAPI.getStatus().catch(() => null),
      ]);

      setRecentSessions(sessionsRes.data?.data?.sessions || []);
      setTotalChats(sessionsRes.data?.data?.total ?? 0);
      setNotices(noticesRes?.data?.data?.notices || []);

      if (subRes?.data?.success) {
        setChatUsage(subRes.data.data.chatUsage || null);
        setPlanName(subRes.data.data.planName || '');
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setLoadError(t('dashboard.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    try {
      const res = await subscriptionAPI.checkout();
      if (res.data?.success && res.data?.data?.checkoutUrl) {
        window.location.href = res.data.data.checkoutUrl;
      } else {
        setLoadError(res.data?.error || t('dashboard.upgradeError'));
      }
    } catch (err) {
      setLoadError(err.response?.data?.error || t('dashboard.upgradeError'));
    }
  };

  const usagePercent = useMemo(() => {
    if (!chatUsage || chatUsage.unlimited || !chatUsage.dailyLimit) return 0;
    return Math.min(100, Math.round((chatUsage.usedToday / chatUsage.dailyLimit) * 100));
  }, [chatUsage]);

  const usageLow = chatUsage && !chatUsage.unlimited && (chatUsage.remaining ?? 0) <= 5;

  return (
    <Box>
      {/* Hero */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
          color: 'common.white',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'center' }}
          justifyContent="space-between"
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Chip
                size="small"
                label={resolvedPlanName}
                sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'common.white', fontWeight: 600 }}
              />
              {isAdmin && (
                <Chip
                  size="small"
                  icon={<AdminIcon sx={{ color: 'common.white !important' }} />}
                  label={t('dashboard.adminBadge')}
                  sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'common.white' }}
                />
              )}
            </Stack>
            <Typography variant="h4" fontWeight={800} sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
              {t('dashboard.welcome', { name: displayName })}
            </Typography>
            <Typography variant="body1" sx={{ mt: 1, opacity: 0.92, maxWidth: 560 }}>
              {t('dashboard.subtitle')}
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexShrink: 0 }}>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowIcon />}
              onClick={() => navigate('/chat')}
              sx={{
                bgcolor: 'common.white',
                color: 'primary.dark',
                fontWeight: 700,
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              {t('dashboard.startChat')}
            </Button>
            {!isPaid && !isAdmin && (
              <Button
                variant="outlined"
                size="large"
                startIcon={<UpgradeIcon />}
                onClick={handleUpgrade}
                sx={{
                  borderColor: 'rgba(255,255,255,0.7)',
                  color: 'common.white',
                  '&:hover': { borderColor: 'common.white', bgcolor: 'rgba(255,255,255,0.08)' },
                }}
              >
                {t('dashboard.upgrade')}
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLoadError('')}>
          {loadError}
        </Alert>
      )}

      {/* Quick actions */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
        {t('dashboard.quickActions')}
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <QuickAction
            icon={ChatIcon}
            title={t('dashboard.actions.chat')}
            description={t('dashboard.actions.chatDesc')}
            onClick={() => navigate('/chat')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <QuickAction
            icon={UploadIcon}
            title={t('dashboard.actions.upload')}
            description={t('dashboard.actions.uploadDesc')}
            onClick={() => navigate('/chat')}
            color="secondary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <QuickAction
            icon={MyPageIcon}
            title={t('dashboard.actions.mypage')}
            description={t('dashboard.actions.mypageDesc')}
            onClick={() => navigate('/mypage')}
            color="text.secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <QuickAction
            icon={NoticeIcon}
            title={t('dashboard.actions.notices')}
            description={t('dashboard.actions.noticesDesc')}
            onClick={() => navigate('/notices')}
            color="warning.dark"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Usage */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                {t('dashboard.usage.title')}
              </Typography>
              {loading ? (
                <Skeleton height={96} />
              ) : (
                <>
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('dashboard.usage.todayChats')}
                    </Typography>
                    <Typography variant="h5" fontWeight={800}>
                      {chatUsage?.unlimited
                        ? t('dashboard.usage.unlimited')
                        : `${chatUsage?.usedToday ?? 0} / ${chatUsage?.dailyLimit ?? 30}`}
                    </Typography>
                  </Stack>
                  {!chatUsage?.unlimited && (
                    <>
                      <LinearProgress
                        variant="determinate"
                        value={usagePercent}
                        color={usageLow ? 'warning' : 'primary'}
                        sx={{ height: 8, borderRadius: 4, mb: 1 }}
                      />
                      <Typography variant="caption" color={usageLow ? 'warning.dark' : 'text.secondary'}>
                        {usageLow
                          ? t('dashboard.usage.remainingLow', { n: chatUsage?.remaining ?? 0 })
                          : t('dashboard.usage.remaining', { n: chatUsage?.remaining ?? 0 })}
                      </Typography>
                    </>
                  )}
                  {chatUsage?.unlimited && (
                    <Typography variant="body2" color="text.secondary">
                      {t('dashboard.usage.unlimitedHint')}
                    </Typography>
                  )}
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {t('dashboard.usage.totalSessions')}
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {totalChats.toLocaleString()}
                    </Typography>
                  </Stack>
                  {!isPaid && !isAdmin && (
                    <Button
                      fullWidth
                      sx={{ mt: 2 }}
                      variant="outlined"
                      startIcon={<UpgradeIcon />}
                      onClick={handleUpgrade}
                    >
                      {t('dashboard.usage.upgradeCta')}
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent chats */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6" fontWeight={700}>
                  {t('dashboard.recentChats')}
                </Typography>
                <Button size="small" onClick={() => navigate('/chat')}>
                  {t('dashboard.viewAll')}
                </Button>
              </Stack>
              {loading ? (
                <Box>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} height={48} sx={{ mb: 1 }} />
                  ))}
                </Box>
              ) : recentSessions.length === 0 ? (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('dashboard.emptyChats')}
                  </Typography>
                  <Button variant="contained" onClick={() => navigate('/chat')}>
                    {t('dashboard.startChat')}
                  </Button>
                </Box>
              ) : (
                <List dense disablePadding>
                  {recentSessions.map((session) => (
                    <ListItemButton
                      key={session.id}
                      onClick={() => navigate('/chat')}
                      sx={{ borderRadius: 1, px: 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <ChatIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={session.title || t('dashboard.untitledChat')}
                        secondary={formatRelativeTime(session.updated_at || session.created_at, t)}
                        primaryTypographyProps={{ noWrap: true, fontWeight: 600 }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Notices */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6" fontWeight={700}>
                  {t('dashboard.recentNotices')}
                </Typography>
                <Button size="small" onClick={() => navigate('/notices')}>
                  {t('dashboard.viewAll')}
                </Button>
              </Stack>
              {loading ? (
                <Box>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} height={48} sx={{ mb: 1 }} />
                  ))}
                </Box>
              ) : notices.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                  {t('dashboard.emptyNotices')}
                </Typography>
              ) : (
                <List dense disablePadding>
                  {notices.map((notice) => (
                    <ListItemButton
                      key={notice.id}
                      onClick={() => navigate('/notices')}
                      sx={{ borderRadius: 1, px: 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <NoticeIcon fontSize="small" color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            {(notice.is_pinned || notice.is_important) && (
                              <Chip
                                size="small"
                                label={notice.is_pinned ? t('dashboard.pinned') : t('dashboard.important')}
                                color={notice.is_pinned ? 'primary' : 'warning'}
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {notice.title}
                            </Typography>
                          </Stack>
                        }
                        secondary={formatRelativeTime(notice.created_at || notice.published_at, t)}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* How to use */}
      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            {t('dashboard.howto.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('dashboard.howto.subtitle')}
          </Typography>
          <Grid container spacing={2}>
            {[
              { step: 1, icon: UploadIcon, title: t('dashboard.howto.step1'), desc: t('dashboard.howto.step1Desc') },
              { step: 2, icon: DocIcon, title: t('dashboard.howto.step2'), desc: t('dashboard.howto.step2Desc') },
              { step: 3, icon: ExportIcon, title: t('dashboard.howto.step3'), desc: t('dashboard.howto.step3Desc') },
            ].map(({ step, icon: Icon, title, desc }) => (
              <Grid item xs={12} md={4} key={step}>
                <Box
                  sx={{
                    p: 2,
                    height: '100%',
                    borderRadius: 2,
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'grey.200',
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        color: 'common.white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 13,
                        fontWeight: 800,
                      }}
                    >
                      {step}
                    </Box>
                    <Icon color="primary" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight={700}>
                      {title}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" endIcon={<ArrowIcon />} onClick={() => navigate('/chat')}>
              {t('dashboard.howto.cta')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {isAdmin && (
        <Alert
          severity="info"
          sx={{ mt: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/platform')}>
              {t('dashboard.openPlatform')}
            </Button>
          }
        >
          {t('dashboard.adminHint')}
        </Alert>
      )}
    </Box>
  );
}
